import * as React from "react";
import { Check, ChevronsUpDown, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { SA_SUBURBS } from "@/lib/suburbs";

export function SuburbSelect({ value, onSelect, placeholder = "Select suburb...", className }) {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");

    // Memoize and slice to prevent rendering thousands of DOM nodes at once
    const filteredSuburbs = React.useMemo(() => {
        if (!search) return SA_SUBURBS.slice(0, 50);
        const lowerSearch = search.toLowerCase();
        return SA_SUBURBS.filter(s => s.toLowerCase().includes(lowerSearch)).slice(0, 50);
    }, [search]);

    return (
        <Popover open={open} onOpenChange={setOpen} modal={true}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn("w-full justify-between rounded-xl bg-white/50 border-none h-10", className)}
                >
                    <div className="flex items-center gap-2 truncate">
                        <MapPin className="w-4 h-4 shrink-0 text-muted-foreground" />
                        <span className="truncate">
                            {value ? SA_SUBURBS.find((s) => s.toLowerCase() === value.toLowerCase()) || value : placeholder}
                        </span>
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 rounded-xl" align="start">
                <Command className="rounded-xl" shouldFilter={false}>
                    <CommandInput placeholder="Search suburb..." className="h-9" value={search} onValueChange={setSearch} />
                    <CommandList>
                        <CommandEmpty>No suburb found.</CommandEmpty>
                        <CommandGroup>
                            <CommandItem
                                value="all"
                                onSelect={() => {
                                    onSelect("");
                                    setOpen(false);
                                }}
                                className="cursor-pointer text-primary font-medium"
                            >
                                <Check
                                    className={cn(
                                        "mr-2 h-4 w-4",
                                        !value || value === "" ? "opacity-100" : "opacity-0"
                                    )}
                                />
                                All Suburbs / Clear
                            </CommandItem>
                            {filteredSuburbs.map((suburb, index) => (
                                <CommandItem
                                    key={`${suburb}-${index}`}
                                    value={suburb}
                                    onSelect={(currentValue) => {
                                        onSelect(currentValue === value ? "" : currentValue);
                                        setOpen(false);
                                    }}
                                    className="cursor-pointer"
                                >
                                    <Check
                                        className={cn(
                                            "mr-2 h-4 w-4",
                                            value?.toLowerCase() === suburb.toLowerCase() ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                    {suburb}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
